import gql from 'graphql-tag'
import {useQuery} from 'react-apollo'
import {Client} from '../../../../../graphql/types'
import {PAMM_OFFER_TREE_FIELDS} from '../../../../PammOffersTree/pammOfferFragment'

/**
 * Fetches a Money Manager's PAMM offers for the hierarchical tree view.
 *
 * Uses the GraphQL `pammOffers` field (rather than the REST endpoint the page used
 * previously) because it returns the parsed `agentChain` the tree needs to rebuild the
 * offer hierarchy. `pammOffers` returns the manager's whole network, so the flat list can
 * be assembled into a tree on the client.
 */
const ACCOUNTS_WITH_PAMM_OFFERS = gql`
  query AccountsWithPammManagerOffers {
    viewer {
      ... on Client {
        id
        accounts(hidden: false, rejected: false) {
          ... on BaseAccount {
            id
            pammOffers {
              ...PammOfferTreeFields
            }
          }
        }
      }
    }
  }
  ${PAMM_OFFER_TREE_FIELDS}
`

const isNotNil = <T>(value: T): value is NonNullable<T> => value !== null && typeof value !== 'undefined'

export const usePammManagerOffers = (managerAccountId: number) => {
  // `errorPolicy: 'all'` is load-bearing here, not defensive. This query asks for `pammOffers` on
  // EVERY account the viewer holds, and `pammOffers` on a PAMM *investor* account returns that
  // investor's manager's offers — nodes this viewer does not own. The server denies `investors` on
  // those with an authorization error (PT-5708), and under the default policy ('none') Apollo
  // discards `data` entirely whenever any GraphQL error is present. A Money Manager who also
  // invests in someone else's PAMM would then see a blank page instead of their own tree. Keep the
  // data; the denied nodes simply arrive with `investors: null`.
  const {loading, data, error} = useQuery<{viewer: Client}>(ACCOUNTS_WITH_PAMM_OFFERS, {errorPolicy: 'all'})

  const managerAccount = (data?.viewer?.accounts ?? []).find(
    (account) => account?.id === managerAccountId,
  )
  const offers = (managerAccount?.pammOffers ?? []).filter(isNotNil)

  // A per-field authorization error on a foreign node is expected and already handled by dropping
  // that node's book. Only surface `error` when it actually cost us the tree.
  return {error: offers.length === 0 ? error : undefined, loading, offers}
}
